<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { CsIcon } from '../icon';
import { CsListboxItem } from '../listbox';
import { CsSpinner } from '../spinner';

/** @typedef {import('../icon/icon-types').IconName} IconName */

/**
 * @typedef {object} ListboxItemConfirmationProps
 * @property {Function} [onConfirmFn] - Function to execute when confirmation is confirmed
 * @property {string} [label=''] - Text label for the delete action
 * @property {IconName} [labelIcon='delete'] - Icon name for the delete action
 * @property {string} [labelConfirm='Confirm'] - Text label for the confirmation state
 * @property {IconName} [labelIconConfirm='check'] - Icon name for the confirmation state
 * @property {string} [labelLoading='Loading'] - Text label for the loading state
 * @property {('critical'|'default')} [variant='critical'] - Variant of the listbox item
 */

/**
 * @type {ListboxItemConfirmationProps}
 */
const props = defineProps({
  onConfirmFn: {
    type: Function,
    default: () => {},
  },
  label: {
    type: String,
    default: '',
  },
  labelIcon: {
    type: String,
    default: 'delete',
  },
  labelConfirm: {
    type: String,
    default: 'Confirm',
  },
  labelIconConfirm: {
    type: String,
    default: 'check',
  },
  labelLoading: {
    type: String,
    default: 'Loading',
  },
  variant: {
    type: String,
    default: 'critical',
  },
});

const emit = defineEmits(['finish']);

const isLoading = ref(false);
const isShowConfirmation = ref(false);
let confirmationTimeout = null;

const content = computed(() => {
  if (isLoading.value) return 'loading';
  if (isShowConfirmation.value) return 'confirm';

  return 'default';
});

async function handleClick() {
  if (isLoading.value) return;
  if (!isShowConfirmation.value) {
    isShowConfirmation.value = true;

    // Revert back after 5 dont it anything
    confirmationTimeout = setTimeout(() => {
      isShowConfirmation.value = false;
    }, 5000);

    return;
  }

  // Deactive set timeout
  if (confirmationTimeout) {
    clearTimeout(confirmationTimeout);
    confirmationTimeout = null;
  }

  isLoading.value = true;
  await props.onConfirmFn();
  isLoading.value = false;

  emit('finish');
}

onBeforeUnmount(() => {
  // Clear timeout
  if (confirmationTimeout) {
    clearTimeout(confirmationTimeout);
    confirmationTimeout = null;
  }
});
</script>

<template>
  <CsListboxItem
    :variant="isLoading.value ? 'default' : props.variant"
    :is-disabled="isLoading"
    @click="handleClick"
  >
    <template v-if="content === 'default'">
      <CsIcon v-if="props.labelIcon" :name="props.labelIcon" />
      {{ props.label }}
    </template>

    <template v-else-if="content === 'confirm'">
      <CsIcon v-if="props.labelIconConfirm" :name="props.labelIconConfirm" />
      {{ props.labelConfirm }}
    </template>

    <template v-else-if="content === 'loading'">
      <CsSpinner />

      {{ props.labelLoading }}
    </template>
  </CsListboxItem>
</template>
